import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Description as ReportIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Link, useParams } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';

const STATUS_OPTIONS = [
  { value: 'any', label: 'All' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'overridden', label: 'Overridden' },
  { value: 'conflict', label: 'Conflict' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'awaiting-other', label: 'Awaiting other team' },
];

const formatGameDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const reportStatusLabel = (status) => {
  switch (status) {
    case 2: return 'Submitted';
    case 4: return 'Overridden';
    default: return 'Unknown';
  }
};

const reportStatusColor = (status) => {
  switch (status) {
    case 2: return 'success';
    case 4: return 'warning';
    default: return 'default';
  }
};

const LeagueMatchReportsPage = () => {
  const { leagueId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [reports, setReports] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [teamFilter, setTeamFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('any');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadTeams = async () => {
      try {
        const data = await unifiedApi.getLeagueTeamsWithManagers(leagueId);
        if (!cancelled) setTeams(data || []);
      } catch {
        // Non-fatal — filter just won't be populated
      }
    };
    loadTeams();
    return () => { cancelled = true; };
  }, [leagueId]);

  useEffect(() => {
    let cancelled = false;
    const loadReports = async () => {
      try {
        setLoading(true);
        const filters = {};
        if (teamFilter) filters.teamId = teamFilter;
        if (statusFilter && statusFilter !== 'any') filters.status = statusFilter;
        if (fromDate) filters.from = fromDate;
        if (toDate) filters.to = toDate;
        const data = await unifiedApi.getLeagueMatchReports(leagueId, filters);
        if (!cancelled) {
          setReports(data || []);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load match reports.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadReports();
    return () => { cancelled = true; };
  }, [leagueId, teamFilter, statusFilter, fromDate, toDate]);

  const summaryCounts = useMemo(() => {
    let conflicts = 0;
    let finalized = 0;
    let awaiting = 0;
    let overridden = 0;
    for (const r of reports) {
      if (r.gameFinalized) finalized++;
      if (r.bothReportsSubmitted && !r.scoresMatch && !r.gameFinalized) conflicts++;
      if (!r.bothReportsSubmitted && !r.gameFinalized) awaiting++;
      if (r.homeTeamReport?.status === 4 || r.awayTeamReport?.status === 4) overridden++;
    }
    return { conflicts, finalized, awaiting, overridden, total: reports.length };
  }, [reports]);

  const renderRowChips = (report) => {
    const chips = [];
    const noop = () => {};
    if (report.gameFinalized) {
      chips.push(<Chip key="fin" label="Finalized" color="info" size="small" variant="outlined" clickable={false} onClick={noop} />);
    }
    if (report.bothReportsSubmitted && !report.scoresMatch && !report.gameFinalized) {
      chips.push(<Chip key="conflict" label="Conflict" color="error" size="small" clickable={false} onClick={noop} />);
    }
    if (!report.bothReportsSubmitted && !report.gameFinalized) {
      chips.push(<Chip key="awaiting" label="Awaiting other" color="warning" size="small" variant="outlined" clickable={false} onClick={noop} />);
    }
    if (report.homeTeamReport?.status === 4 || report.awayTeamReport?.status === 4) {
      chips.push(<Chip key="overridden" label="Overridden" color="warning" size="small" clickable={false} onClick={noop} />);
    }
    return chips.length
      ? chips
      : <Chip label="Submitted" color="success" size="small" variant="outlined" clickable={false} onClick={noop} />;
  };

  const renderTeamReportCell = (report, teamId) => {
    if (!report) {
      return (
        <Typography variant="body2" color="text.secondary">
          Not submitted
        </Typography>
      );
    }
    return (
      <Stack alignItems={isMobile ? 'flex-start' : 'center'} spacing={0.5}>
        <Typography fontWeight={600}>
          {report.homeGoals} – {report.awayGoals}
        </Typography>
        <Chip
          label={reportStatusLabel(report.status)}
          color={reportStatusColor(report.status)}
          size="small"
          variant="outlined"
          clickable={false}
          onClick={() => {}}
        />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={0.5}
          sx={{ flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}
        >
          <Button
            size="small"
            variant="text"
            component={Link}
            to={`/games/${report.gameId ?? ''}/teams/${teamId}/report`}
            startIcon={<ViewIcon />}
            fullWidth={isMobile}
          >
            View
          </Button>
          <Button
            size="small"
            variant="text"
            color="primary"
            component={Link}
            to={`/games/${report.gameId ?? ''}/teams/${teamId}/report/edit`}
            startIcon={<EditIcon />}
            fullWidth={isMobile}
          >
            Edit
          </Button>
        </Stack>
      </Stack>
    );
  };

  const renderFilters = () => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
          <InputLabel id="team-filter-label">Team</InputLabel>
          <Select
            labelId="team-filter-label"
            label="Team"
            value={teamFilter}
            onChange={(e) => setTeamFilter(e.target.value)}
          >
            <MenuItem value=""><em>All teams</em></MenuItem>
            {teams.map((t) => (
              <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="From"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="text"
          onClick={() => {
            setTeamFilter('');
            setStatusFilter('any');
            setFromDate('');
            setToDate('');
          }}
        >
          Reset
        </Button>
      </Stack>
    </Paper>
  );

  const renderSummary = () => (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      sx={{ mb: 2 }}
      data-testid="match-reports-summary"
    >
      <Chip label={`Total: ${summaryCounts.total}`} variant="outlined" />
      <Chip label={`Conflicts: ${summaryCounts.conflicts}`} color="error" variant="outlined" />
      <Chip label={`Awaiting: ${summaryCounts.awaiting}`} color="warning" variant="outlined" />
      <Chip label={`Overridden: ${summaryCounts.overridden}`} color="warning" variant="outlined" />
      <Chip label={`Finalized: ${summaryCounts.finalized}`} color="info" variant="outlined" />
    </Stack>
  );

  return (
    <AdminLayout title="Match Reports" leagueId={leagueId}>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
            <ReportIcon color="primary" />
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={600}>
              Match Reports
            </Typography>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {renderFilters()}
          {renderSummary()}

          {reports.length === 0 && !error && (
            <Alert severity="info">No match reports found for the current filters.</Alert>
          )}

          {/* Desktop Table */}
          {reports.length > 0 && !isMobile && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Match</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Home Report</TableCell>
                    <TableCell align="center">Away Report</TableCell>
                    <TableCell align="center">Final</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reports.map((report) => (
                    <TableRow key={report.gameId} hover>
                      <TableCell>{formatGameDate(report.gameDate)}</TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>
                          {report.homeTeamName} vs {report.awayTeamName || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {renderRowChips(report)}
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        {renderTeamReportCell(
                          report.homeTeamReport ? { ...report.homeTeamReport, gameId: report.gameId } : null,
                          report.homeTeamId,
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {report.awayTeamId
                          ? renderTeamReportCell(
                              report.awayTeamReport ? { ...report.awayTeamReport, gameId: report.gameId } : null,
                              report.awayTeamId,
                            )
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                      <TableCell align="center">
                        {report.gameFinalized
                          ? <Typography fontWeight={600}>{report.finalHomeGoals} – {report.finalAwayGoals}</Typography>
                          : <Typography variant="body2" color="text.secondary">—</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Mobile Cards */}
          {reports.length > 0 && isMobile && (
            <Stack spacing={1.5}>
              {reports.map((report) => (
                <Card key={report.gameId} variant="outlined">
                  <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatGameDate(report.gameDate)}
                    </Typography>
                    <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                      {report.homeTeamName} vs {report.awayTeamName || '—'}
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
                      {renderRowChips(report)}
                    </Stack>
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {report.homeTeamName} report
                        </Typography>
                        {renderTeamReportCell(
                          report.homeTeamReport ? { ...report.homeTeamReport, gameId: report.gameId } : null,
                          report.homeTeamId,
                        )}
                      </Box>
                      {report.awayTeamId && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {report.awayTeamName} report
                          </Typography>
                          {renderTeamReportCell(
                            report.awayTeamReport ? { ...report.awayTeamReport, gameId: report.gameId } : null,
                            report.awayTeamId,
                          )}
                        </Box>
                      )}
                      {report.gameFinalized && (
                        <Typography variant="body2">
                          <strong>Final:</strong> {report.finalHomeGoals} – {report.finalAwayGoals}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </>
      )}
    </AdminLayout>
  );
};

export default LeagueMatchReportsPage;
